/**
 * Positioning math for a GPS track drawn over a Google Static Maps image.
 * Uses Google's documented spherical web-mercator tile scheme — pure angle
 * math with no ellipsoid/radius constants — because marker pixels must land
 * exactly where the Static Maps API draws the same coordinates.
 *
 * Copied from draft-editor 17.5.0 `src/components/LveVideoPlayer/locationTrack.ts`,
 * which renders the same track in the recorded player. The copy exists so this
 * app does not take a dependency on a 400-file CAD editor for 140 lines of
 * frozen geometry; `__tests__/locationGeometry.parity.json` holds vectors
 * generated from that file and pins the two implementations together.
 * Regenerate the fixture from the draft-editor checkout if its math ever moves.
 *
 * Only what a live map needs was carried over. Playback-time lookups
 * (`trackPointAt`, `trackOffsetAtMapPosition`) and the path-carrying
 * `buildStaticMapUrl` stayed behind: a live map always draws its newest fix
 * and paints the trail itself — see `staticBasemapUrl`.
 */

const TILE_SIZE = 256;
const MIN_ZOOM = 1;
const MAX_ZOOM = 20;
const FIT_PADDING = 1.4;
export const STATIC_MAP_BASE_URL = 'https://maps.googleapis.com/maps/api/staticmap';
const STATIC_MAP_MAX_DIMENSION = 640;
const HEADING_MAX_GAP_SEC = 20;

export interface LocationFix {
  /** Seconds since the first fix of the call. */
  offset: number;
  lat: number;
  lng: number;
  accuracy?: number | null;
  /** Compass heading in degrees clockwise from north, when the device reported one. */
  heading?: number | null;
}

interface WorldPoint {
  x: number;
  y: number;
}

export interface MapGeometry {
  center: WorldPoint;
  zoom: number;
  width: number;
  height: number;
}

export interface MapBoxSize {
  width: number;
  height: number;
}

export interface MapPositionPercent {
  left: number;
  top: number;
}

/** Projects lat/lng onto the [0..1]² web-mercator world square Google tiles use. */
export const toWorldPoint = (lat: number, lng: number): WorldPoint => {
  const latRadians = (lat * Math.PI) / 180;
  const mercatorY = Math.log(Math.tan(Math.PI / 4 + latRadians / 2));

  return {
    x: (lng + 180) / 360,
    y: (1 - mercatorY / Math.PI) / 2,
  };
};

export const fitTrackGeometry = (track: LocationFix[], width: number, height: number): MapGeometry => {
  const worldPoints = track.map(point => toWorldPoint(point.lat, point.lng));
  const minX = Math.min(...worldPoints.map(point => point.x));
  const maxX = Math.max(...worldPoints.map(point => point.x));
  const minY = Math.min(...worldPoints.map(point => point.y));
  const maxY = Math.max(...worldPoints.map(point => point.y));

  const spanX = (maxX - minX) * FIT_PADDING;
  const spanY = (maxY - minY) * FIT_PADDING;
  const zoomForSpan = (span: number, sizePx: number) =>
    (span > 0 ? Math.floor(Math.log2(sizePx / (span * TILE_SIZE))) : MAX_ZOOM);
  const zoom = Math.min(
    Math.max(Math.min(zoomForSpan(spanX, width), zoomForSpan(spanY, height)), MIN_ZOOM),
    MAX_ZOOM,
  );

  return {
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
    zoom,
    width,
    height,
  };
};

export const positionPercent = (
  point: Pick<LocationFix, 'lat' | 'lng'>,
  geometry: MapGeometry,
): MapPositionPercent => {
  const world = toWorldPoint(point.lat, point.lng);
  const scale = TILE_SIZE * 2 ** geometry.zoom;

  return {
    left: (((world.x - geometry.center.x) * scale + geometry.width / 2) / geometry.width) * 100,
    top: (((world.y - geometry.center.y) * scale + geometry.height / 2) / geometry.height) * 100,
  };
};

/** Where a fix lands inside the map image, as percentages of its width/height. */
export const markerPositionPercent = (point: LocationFix, geometry: MapGeometry): MapPositionPercent => {
  const { left, top } = positionPercent(point, geometry);
  const clamp = (value: number) => Math.min(Math.max(value, 0), 100);

  return { left: clamp(left), top: clamp(top) };
};

type HeadingFix = LocationFix & { heading: number };

const hasHeading = (point: LocationFix): point is HeadingFix => typeof point.heading === 'number';

const interpolateHeading = (before: HeadingFix, after: HeadingFix, seconds: number): number => {
  const span = after.offset - before.offset;
  const ratio = span > 0 ? (seconds - before.offset) / span : 0;
  const arc = ((after.heading - before.heading + 540) % 360) - 180;

  return (before.heading + arc * ratio + 360) % 360;
};

/**
 * Compass heading at a track offset, when nearby fixes reported one.
 * Interpolates along the shortest arc so 350° → 10° turns through north
 * instead of spinning the long way round.
 */
const neighboursOf = (
  fixes: HeadingFix[], seconds: number,
): [HeadingFix | undefined, HeadingFix | undefined] => {
  const nextIndex = fixes.findIndex(fix => fix.offset >= seconds);
  if (nextIndex === -1) {
    return [fixes[fixes.length - 1], undefined];
  }

  return [fixes[nextIndex - 1], fixes[nextIndex]];
};

const nearbyHeading = (nearest: HeadingFix | undefined, seconds: number): number | undefined => {
  if (nearest !== undefined && Math.abs(nearest.offset - seconds) <= HEADING_MAX_GAP_SEC) {
    return nearest.heading;
  }

  return undefined;
};

export const headingAt = (track: LocationFix[], seconds: number): number | undefined => {
  const [before, after] = neighboursOf(track.filter(hasHeading), seconds);

  if (before && after) {
    return interpolateHeading(before, after, seconds);
  }

  return nearbyHeading(before ?? after, seconds);
};

/**
 * The Static Maps request size for a displayed box: its exact aspect fitted
 * into the API's 640×640 logical-pixel cap (scale=2 doubles the real
 * resolution). Matching aspects keeps the percent-based marker math true.
 */
export const mapRequestSize = (box: MapBoxSize): MapBoxSize => {
  const scale = Math.min(STATIC_MAP_MAX_DIMENSION / box.width, STATIC_MAP_MAX_DIMENSION / box.height, 1);

  return {
    width: Math.max(Math.round(box.width * scale), 1),
    height: Math.max(Math.round(box.height * scale), 1),
  };
};

export const satelliteMapUrl = (center: string, zoom: number, size: MapBoxSize, apiKey: string): string => {
  const params = new URLSearchParams({
    center,
    zoom: String(zoom),
    size: `${size.width}x${size.height}`,
    scale: '2',
    maptype: 'satellite',
    key: apiKey,
  });

  return `${STATIC_MAP_BASE_URL}?${params.toString()}`;
};

const centerToLatLng = (center: WorldPoint): { lat: number; lng: number } => {
  const mercatorY = (1 - center.y * 2) * Math.PI;

  return {
    lat: ((2 * Math.atan(Math.exp(mercatorY)) - Math.PI / 2) * 180) / Math.PI,
    lng: center.x * 360 - 180,
  };
};

/**
 * The satellite basemap for a geometry, carrying no route.
 *
 * The recorded player bakes the walked path into this URL, which it can afford
 * because its track never changes. A live track grows every few seconds, and
 * every changed URL is another billable Static Maps request — so the live map
 * asks only for ground, and draws the route over it in the DOM.
 */
export const staticBasemapUrl = (geometry: MapGeometry, apiKey: string): string => {
  const center = centerToLatLng(geometry.center);

  return satelliteMapUrl(`${center.lat.toFixed(6)},${center.lng.toFixed(6)}`, geometry.zoom, geometry, apiKey);
};
